/**
 * Organizations Service
 * Handles organization management, members, and credits
 */

import type { GatewayClient } from './client'
import type {
  Organization,
  OrganizationListResponse,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  OrganizationMembersResponse,
  InviteMemberRequest,
  UpdateMemberRoleRequest,
  OrgCreditsResponse,
  OrgCreditHistoryResponse,
  PurchaseCreditsRequest,
  CheckoutResponse,
  OrgStorageAggregation,
} from './types'

// ============================================================================
// Types
// ============================================================================

export interface OrganizationsService {
  /**
   * List all organizations for the current user
   */
  listOrganizations(): Promise<OrganizationListResponse>

  /**
   * Create a new organization
   */
  createOrganization(name: string): Promise<Organization>

  /**
   * Get a specific organization by ID
   */
  getOrganization(orgId: string): Promise<Organization>

  /**
   * Update an organization's details
   */
  updateOrganization(orgId: string, name: string): Promise<Organization>

  /**
   * Delete an organization
   */
  deleteOrganization(orgId: string): Promise<void>

  /**
   * List members of an organization
   */
  listMembers(orgId: string): Promise<OrganizationMembersResponse>

  /**
   * Invite a user to an organization
   */
  inviteMember(
    orgId: string,
    userId: string,
    role: string
  ): Promise<{ message: string }>

  /**
   * Remove a member from an organization
   */
  removeMember(orgId: string, userId: string): Promise<void>

  /**
   * Update a member's role in an organization
   */
  updateMemberRole(
    orgId: string,
    userId: string,
    role: string
  ): Promise<{ message: string }>

  /**
   * Get organization credit balance and details
   */
  getCredits(orgId: string): Promise<OrgCreditsResponse>

  /**
   * Get organization credit transaction history
   */
  getCreditHistory(orgId: string): Promise<OrgCreditHistoryResponse>

  /**
   * Purchase credits for an organization
   */
  purchaseCredits(
    orgId: string,
    packId: string,
    returnUrl: string
  ): Promise<CheckoutResponse>

  /**
   * Get aggregated storage information for an organization
   */
  getOrgStorage(orgId: string): Promise<OrgStorageAggregation>
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create an OrganizationsService instance
 */
export function createOrganizationsService(
  client: GatewayClient
): OrganizationsService {
  return {
    async listOrganizations(): Promise<OrganizationListResponse> {
      return client.get<OrganizationListResponse>('/v1/organizations')
    },

    async createOrganization(name: string): Promise<Organization> {
      const request: CreateOrganizationRequest = { name }
      return client.post<Organization>('/v1/organizations', request)
    },

    async getOrganization(orgId: string): Promise<Organization> {
      return client.get<Organization>(`/v1/organizations/${orgId}`)
    },

    async updateOrganization(
      orgId: string,
      name: string
    ): Promise<Organization> {
      const request: UpdateOrganizationRequest = { name }
      return client.patch<Organization>(`/v1/organizations/${orgId}`, request)
    },

    async deleteOrganization(orgId: string): Promise<void> {
      await client.delete<void>(`/v1/organizations/${orgId}`)
    },

    async listMembers(orgId: string): Promise<OrganizationMembersResponse> {
      return client.get<OrganizationMembersResponse>(
        `/v1/organizations/${orgId}/members`
      )
    },

    async inviteMember(
      orgId: string,
      userId: string,
      role: string
    ): Promise<{ message: string }> {
      const request: InviteMemberRequest = { userId, role }
      return client.post<{ message: string }>(
        `/v1/organizations/${orgId}/members/invite`,
        request
      )
    },

    async removeMember(orgId: string, userId: string): Promise<void> {
      await client.delete<void>(`/v1/organizations/${orgId}/members/${userId}`)
    },

    async updateMemberRole(
      orgId: string,
      userId: string,
      role: string
    ): Promise<{ message: string }> {
      const request: UpdateMemberRoleRequest = { role }
      return client.patch<{ message: string }>(
        `/v1/organizations/${orgId}/members/${userId}/role`,
        request
      )
    },

    async getCredits(orgId: string): Promise<OrgCreditsResponse> {
      return client.get<OrgCreditsResponse>(
        `/v1/organizations/${orgId}/credits`
      )
    },

    async getCreditHistory(orgId: string): Promise<OrgCreditHistoryResponse> {
      return client.get<OrgCreditHistoryResponse>(
        `/v1/organizations/${orgId}/credits/history`
      )
    },

    async purchaseCredits(
      orgId: string,
      packId: string,
      returnUrl: string
    ): Promise<CheckoutResponse> {
      const request: PurchaseCreditsRequest = { packId, returnUrl }
      return client.post<CheckoutResponse>(
        `/v1/organizations/${orgId}/credits/purchase`,
        request
      )
    },

    async getOrgStorage(orgId: string): Promise<OrgStorageAggregation> {
      return client.get<OrgStorageAggregation>(
        `/v1/organizations/${orgId}/storage`
      )
    },
  }
}
